export const deploymentArticles = [
  {
    slug: 'supported-platforms-and-cameras',
    title: 'Supported platforms and cameras',
    summary: 'Choose a deployment path, host, and camera that fit the measurements you need before equipment reaches the Site.',
    category: 'deployment',
    sources: [
      { file: 'product-os-and-hardware-support.pdf' },
      { file: 'audience-cameras.pdf' },
      { file: 'traffic---supported-cameras.pdf' },
      { file: 'traffic-hardware-requirements---windowslinux.pdf' }
    ],
    sections: [
      {
        id: 'choose-solution',
        title: 'Choose a solution',
        paragraphs: [
          'Begin with the question the installation needs to answer, because the solution shapes every hardware decision that follows. Audience measures face-based attention and supported demographic attributes. Traffic measures people or vehicle movement and, where supported, vehicle types, directions, and speed. The selected solution determines the processing capacity, camera, and deployment paths you can use.'
        ],
        tables: [
          {
            caption: 'Solution characteristics',
            headers: ['Solution', 'Camera and processing profile'],
            rows: [
              ['Audience', 'Face-based audience measurement with a suitable USB or IP camera.'],
              ['Traffic', 'People or vehicle movement and supported vehicle classifications from an IP camera.']
            ]
          }
        ]
      },
      {
        id: 'choose-platform',
        title: 'Choose a deployment platform',
        paragraphs: [
          'AdMobilize Vision has installation guides for Windows, Linux, BalenaOS, and supported BrightSign players. Each path fits a different operating model, and support varies by solution, operating-system release, architecture, and Device model.',
          'Confirm the current approved combination with the deployment owner before ordering hardware or preparing installation media. Windows and Linux hosts are normally client managed, while BalenaOS Devices can use the managed deployment workflow.'
        ],
        paragraphLinks: [
          { label: 'Windows', slug: 'windows-installation', section: 'prepare' },
          { label: 'Linux', slug: 'linux-on-device', section: 'prepare' },
          { label: 'BalenaOS', slug: 'balena-sensor-setup', section: 'requirements' },
          { label: 'BrightSign', slug: 'brightsign-installation', section: 'pre-installation' }
        ]
      },
      {
        id: 'size-host',
        title: 'Size the host',
        paragraphs: [
          'The host needs enough headroom to process the camera feed continuously and handle any other work expected at the Site. Use an SSD, enough memory for the analytics workload, and an Intel graphics-capable processor or another currently approved platform. Confirm capacity against the deployed Traffic measurements and number of camera feeds.'
        ],
        bullets: [
          'Include the requirements of any CMS, media player, or other workload running on the host.',
          'Allow storage for queued data and software updates.',
          'Configure reliable power recovery for unattended installations.',
          'Validate architecture and acceleration support before deployment.'
        ]
      },
      {
        id: 'choose-camera',
        title: 'Choose a camera',
        paragraphs: [
          'A camera is part of the measurement system, not a generic accessory. Audience can use a suitable USB or IP camera, while Traffic uses an IP camera. Choose one that produces a stable image and provides the field of view, zoom, and low-light performance required at the Site. Continue with IP camera configuration when the deployment uses a network stream.'
        ],
        paragraphLinks: [{
          label: 'IP camera configuration',
          slug: 'ip-camera-configuration',
          section: 'configure-video'
        }],
        tables: [
          {
            caption: 'Camera selection guidance',
            headers: ['Requirement', 'Guidance'],
            rows: [
              ['Resolution', 'Use a supported 720p or 1080p stream.'],
              ['Field of view', 'Prefer less than 90 degrees unless the site has been validated for a wider view.'],
              ['IP stream', 'Use a stable RTSP or HTTP stream with a supported codec.'],
              ['Network', 'Prefer wired Ethernet; use PoE equipment appropriate for the camera.'],
              ['Adjustment', 'Use optical or digital zoom when the site requires tighter framing.'],
              ['Interoperability', 'ONVIF can simplify discovery, but Vision still requires a working stream URL.']
            ]
          }
        ]
      },
      {
        id: 'prepare-traffic-network',
        title: 'Prepare a Traffic camera network',
        paragraphs: [
          'A Traffic installation depends on a reliable path between the host and camera. Plan power, switching, cable runs, and weather protection together so the stream remains stable after the installation team leaves the Site.'
        ],
        bullets: [
          'Connect the host and camera to the same switch or router unless the approved network design specifies routing between them.',
          'Connect the camera through the required PoE injector or PoE switch.',
          'Provide Ethernet runs for the camera, host, and PoE equipment.',
          'Use a weather-rated enclosure where the host or network equipment is not otherwise protected.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Devices', to: '/devices' }],
    related: ['windows-installation', 'linux-on-device', 'brightsign-installation', 'ip-camera-configuration'],
    legacy: false
  },
  {
    slug: 'ip-camera-configuration',
    title: 'IP camera configuration',
    summary: 'Turn an IP camera into a stable, verified stream that an AdMobilize Vision Device can use reliably.',
    category: 'deployment',
    sources: [{ file: 'ip-camera-configuration.pdf' }],
    sections: [
      {
        id: 'open-camera-manager',
        title: 'Open the camera manager',
        paragraphs: [
          'Configure the camera on the same local network where you can test it before involving the Vision Device. Connect it to Ethernet and open its management interface from an authorized computer on the camera network. Menu names vary by brand and model, but the same basic settings are available on most supported cameras.'
        ],
        steps: [
          'Open the camera address in a browser.',
          'Open Configuration or Settings.',
          'Set a recognizable camera name and the correct time zone.'
        ]
      },
      {
        id: 'configure-video',
        title: 'Configure the video profile',
        paragraphs: [
          'Configure one dedicated, stable profile for Vision. H.264 at 1280 by 720 and 12 to 15 frames per second is a practical starting point. Choose a quality and bitrate that stay comfortably within the host and local-network capacity; a higher setting is not useful if the stream becomes unreliable.'
        ],
        bullets: [
          'Set the correct time zone before validating the stream.',
          'Enable a timestamp overlay only when the deployment policy and analytics workflow require it.'
        ],
        tables: [
          {
            caption: 'Starting video settings',
            headers: ['Setting', 'Starting value'],
            rows: [
              ['Codec', 'H.264'],
              ['Frame size', '1280 × 720'],
              ['Frame rate', '12 to 15 fps'],
              ['Quality and bitrate', 'The highest stable setting supported by the site network and host.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/ip-camera-video-settings.png',
            alt: 'IP camera video settings showing H.264, 1280 by 720 resolution, frame rate, and bit-rate controls',
            caption: 'Configure one stable H.264 profile for AdMobilize Vision to consume.'
          }
        ]
      },
      {
        id: 'set-view',
        title: 'Set the camera view',
        paragraphs: [
          'Frame the scene while you still have direct access to the camera. If the model supports presets, saving the validated position as the home view makes it easier to recover after an accidental movement or restart.'
        ],
        steps: [
          'Set the required pan, tilt, and zoom.',
          'Save the position as a named preset when the camera supports presets.',
          'Set the saved position as the home view.'
        ]
      },
      {
        id: 'verify-stream',
        title: 'Verify the stream URL',
        paragraphs: [
          'Build the RTSP or HTTP URL for the selected profile and test it in a local player before adding it to a Device. This separates camera or network problems from Vision configuration. Store any URL containing camera credentials as a secret, following the guidance in Network, credentials, and previews.'
        ],
        paragraphLinks: [{
          label: 'Network, credentials, and previews',
          slug: 'network-security-and-previews',
          section: 'access-control'
        }],
        code: 'rtsp://CAMERA_HOST:PORT/STREAM_PATH',
        steps: [
          'Open the network-stream command in a local player such as VLC.',
          'Enter the stream URL and confirm that video plays continuously.',
          'If playback fails, verify the host, port, path, and authentication in the camera manager.',
          'If playback is unstable, reduce frame rate, resolution, quality, or bitrate and test again.'
        ]
      },
      {
        id: 'connect-device',
        title: 'Connect the stream to a Device',
        paragraphs: [
          'Once the stream plays continuously on the local network, add the same verified URL to the Device. For Traffic deployments, finish by checking the physical view and configuring regions and zones.'
        ],
        paragraphLinks: [{
          label: 'configuring regions and zones',
          slug: 'vehicle-camera-positioning',
          section: 'regions-and-zones'
        }],
        steps: [
          'Open the Device in Devices.',
          'Set Video Source/Path to the verified stream URL and save the configuration.',
          'Request a preview and confirm that the expected scene is visible before configuring regions or zones.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Devices', to: '/devices' }],
    related: ['supported-platforms-and-cameras', 'vehicle-camera-positioning', 'network-security-and-previews'],
    legacy: false
  },
  {
    slug: 'vehicle-camera-positioning',
    title: 'Vehicle camera positioning and zones',
    summary: 'Frame a road scene that Traffic analytics can follow, then place regions and zones where vehicles can be counted consistently.',
    category: 'deployment',
    sources: [
      { file: 'ip-camera-positioning---vehicle-detection.pdf' },
      { file: 'ip-camera-positioning---vehicle-recognition.pdf' },
      { file: 'vehicle-recognition.pdf' },
      { file: 'faqs-technical-specifications (1).pdf' }
    ],
    sections: [
      {
        id: 'prepare-site',
        title: 'Prepare the site',
        paragraphs: [
          'Good Traffic data begins with the physical installation. Before mounting the camera, confirm the network and power design, the expected travel path, and who can help validate the final view. Fixing these details during the Site visit is much easier than compensating for them in analytics later.'
        ],
        bullets: [
          'Connect the host and camera through the approved switch, router, and PoE equipment.',
          'Mount the camera high enough to reduce occlusion without losing the details required for classification.',
          'Fix the mount and lens before validating regions and zones.',
          'Arrange deployment support before the site visit when remote validation is required.'
        ]
      },
      {
        id: 'field-of-view',
        title: 'Frame the road',
        paragraphs: [
          'Keep the image horizontal and include the complete travel path through the measurement area. A diagonal view that shows the side and either the front or rear of each vehicle normally preserves useful detail for tracking and classification.',
          'Validate the framing at the actual Site instead of treating a published distance as a guarantee. Usable range changes with the lens, resolution, lane count, mounting angle, and the amount of detail the selected solution needs.'
        ],
        figures: [
          {
            src: 'assets/documentation/vehicle-positioning-correct.png',
            alt: 'Diagonal road view that keeps vehicle bodies visible',
            caption: 'Useful views show the vehicle body and travel path without excessive occlusion.'
          },
          {
            src: 'assets/documentation/vehicle-positioning-avoid.png',
            alt: 'Cluttered road view marked as an unsuitable camera position',
            caption: 'Obstructions, extreme angles, and excessive distance reduce useful detail.'
          }
        ]
      },
      {
        id: 'avoid-obstructions',
        title: 'Avoid occlusion and false detections',
        paragraphs: [
          'Watch how vehicles move through the scene, including busy periods. The goal is to keep each vehicle visible long enough to be tracked before and after it crosses a zone, without static objects or neighboring lanes repeatedly hiding it.'
        ],
        bullets: [
          'Avoid roof-only views and angles that show only the front of a vehicle.',
          'Avoid poles, cables, signs, trees, parked vehicles, and other objects that obscure the road.',
          'Avoid framing in which nearby vehicles repeatedly cover other lanes.',
          'Avoid stop lines when stopped traffic prevents reliable tracking.',
          'Keep complete vehicles visible before and after they cross a zone.'
        ]
      },
      {
        id: 'regions-and-zones',
        title: 'Define regions and zones',
        paragraphs: [
          'A region of interest limits the part of the image processed by the detection model. Inside that region, a zone acts as a line or band that a tracked object crosses to create a directional count. Keep both aligned with the real travel path and away from the edges of the image.'
        ],
        bullets: [
          'Leave enough region space before and after each zone to identify and track an object.',
          'Use a compact region shape around the road area instead of covering unrelated scenery.',
          'The standard Dashboard editor supports up to two or three regions and zones, depending on the Traffic Device configuration.'
        ]
      },
      {
        id: 'validate',
        title: 'Validate the installation',
        paragraphs: [
          'Validation connects the camera view with the numbers that will appear later in Traffic metrics. Use a fresh preview, observe representative conditions, and compare a known period of activity with the processed analytics before treating the installation as complete.'
        ],
        paragraphLinks: [{ label: 'Traffic metrics', slug: 'traffic-metrics', section: 'vehicle-counts-and-speed' }],
        steps: [
          'Request a fresh preview and confirm that each region and zone is inside the usable image.',
          'Observe representative traffic, weather, and lighting conditions.',
          'Confirm that complete vehicles remain visible while crossing each zone.',
          'Compare observed traffic with Dashboard analytics after data is processed.',
          'Adjust the mount, zoom, region, or zone when glare, perspective, or occlusion causes missed detections.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Charts', to: '/charts' }
    ],
    related: ['ip-camera-configuration', 'traffic-metrics', 'vehicle-analytics-setup'],
    legacy: false
  },
  {
    slug: 'windows-installation',
    title: 'AdMobilize Vision on Windows',
    summary: 'Prepare a supported Windows host, install and provision AdMobilize Vision, then verify the camera, services, and Dashboard data together.',
    category: 'deployment',
    sources: [
      { file: 'admobilize-vision-windows-installation---v21.pdf' },
      { file: 'faqs-technical-specifications.pdf' }
    ],
    sections: [
      {
        id: 'prepare',
        title: 'Prepare the Windows host',
        paragraphs: [
          'A reliable Windows deployment starts with a known host state. Use a currently supported 64-bit Windows release on hardware approved for the selected solution. Install Windows updates, restart until no required updates remain, and connect the approved camera. Review Supported platforms and cameras before preparing new hardware.'
        ],
        paragraphLinks: [{
          label: 'Supported platforms and cameras',
          slug: 'supported-platforms-and-cameras',
          section: 'choose-platform'
        }],
        bullets: [
          'Allow additional capacity for a CMS or media player running on the same host.',
          'Use an administrator account for the approved installers.',
          'Confirm outbound network access before installation.',
          'Keep Windows camera permissions and security controls available for verification.'
        ]
      },
      {
        id: 'components',
        title: 'Understand the Windows components',
        paragraphs: [
          'AdMobilize Vision runs as a set of cooperating components. Knowing what each one does makes verification more useful: a camera problem, a stopped model, and a provider that cannot reach the platform may all look like missing data, but they require different checks.'
        ],
        tables: [
          {
            caption: 'Vision components',
            headers: ['Component', 'Purpose'],
            rows: [
              ['AdMobilize Updater', 'Maintains the deployed services and software.'],
              ['AdmProvider', 'Provisions the Device, receives configuration, and sends data to the platform.'],
              ['Malos Vision', 'Runs the computer-vision models.'],
              ['AdMobilize Desktop UI', 'Shows component status, previews, local detections, pending data, and diagnostic information.']
            ]
          }
        ]
      },
      {
        id: 'install',
        title: 'Install the approved package',
        paragraphs: [
          'Use the current package from the authorized release channel. Do not reuse a saved installer from another deployment, because the approved instructions account for the Windows release and runtime generation being installed.'
        ],
        steps: [
          'Obtain the current Windows installers and deployment instructions from the authorized release channel.',
          'Run the approved updater installer as an administrator.',
          'Open Windows Services and confirm that the updater service is running.',
          'Wait for the updater and Vision processes to start.',
          'Install Desktop UI when it is included in the approved deployment package.'
        ]
      },
      {
        id: 'provision',
        title: 'Provision the Device',
        paragraphs: [
          'Provisioning connects the local installation with the correct Dashboard record. A new installation can create a Device from a Site Group provisioning token, while a replacement uses the provisioning token and Device ID of an existing Device.',
          'Generate the command through the approved Device-creation flow for this installation. Do not reuse a command created for another host, and keep its provisioning material out of shared notes and systems.'
        ],
        steps: [
          'Choose whether the host is creating a new Device or replacing an existing installation.',
          'Select the Site Group and solution in the approved provisioning flow.',
          'Run the generated Windows provisioning command on the target host as an administrator.',
          'Clear provisioning material from terminal history, notes, and shared systems.',
          'Open Desktop UI and confirm that the expected Device and solution are running.'
        ]
      },
      {
        id: 'connect-ip-camera',
        title: 'Connect an IP camera',
        paragraphs: [
          'If the deployment uses an IP camera, verify the stream independently before adding it to Vision. IP camera configuration covers the recommended profile and local playback checks.'
        ],
        paragraphLinks: [{
          label: 'IP camera configuration',
          slug: 'ip-camera-configuration',
          section: 'configure-video'
        }],
        steps: [
          'Verify the camera stream in a local player.',
          'Open the Device in Devices and set Video Source/Path to the verified stream URL.',
          'Save the configuration and request a preview.',
          'Store the stream URL as a secret when it contains camera credentials.'
        ]
      },
      {
        id: 'verify',
        title: 'Verify local and cloud data',
        paragraphs: [
          'Verification should follow the data from camera to Dashboard. Confirm that the updater, provider, and Vision processes are running. Use Desktop UI to check the preview or live view, local detections, and pending data, then open Dashboard analytics for the same Device and confirm that recent data arrives.'
        ],
        figures: [
          {
            src: 'assets/documentation/windows-service-verification.png',
            alt: 'Windows Task Manager showing the AdMobilize updater and Vision processes',
            caption: 'Confirm that the updater and Vision processes are running before troubleshooting data.'
          }
        ]
      },
      {
        id: 'network-access',
        title: 'Allow required outbound traffic',
        paragraphs: [
          'Windows installations initiate outbound connections for Device management, models, updates, time synchronization, and telemetry. Plan that access with the Site network team before installation so a blocked service does not appear later as a provisioning or data problem.',
          'Obtain the current Windows allowlist from the authorized deployment channel because endpoint sets vary across runtime generations. Network, credentials, and previews explains the common traffic pattern and secret-handling expectations.'
        ],
        paragraphLinks: [{
          label: 'Network, credentials, and previews',
          slug: 'network-security-and-previews',
          section: 'outbound-access'
        }]
      },
      {
        id: 'troubleshoot',
        title: 'Troubleshoot common Windows issues',
        tables: [
          {
            caption: 'Windows checks',
            headers: ['Symptom', 'Checks'],
            rows: [
              ['Black preview', 'Check camera permissions, antivirus controls, the USB port or powered extension, physical obstruction, and whether another application owns the camera.'],
              ['Local detections but no Dashboard data', 'Check the selected Device and date range, network access, pending data, and provider status.'],
              ['Preview window returns after closing', 'Review the solution Show GUI setting for the Device.'],
              ['Vision reports missing media DLLs', 'Confirm whether the Windows edition requires the Microsoft media features supported for that release.'],
              ['Software returns after uninstall', 'Have an administrator review the updater service and scheduled updater task before removing the installation.']
            ]
          }
        ],
        paragraphs: [
          'A Device stores detections locally during a temporary network interruption and resumes sending when connectivity returns. During a longer outage, monitor available storage and pending data. If the Device is online but analytics still look incomplete, follow the checks in Why Data May Be Missing or Delayed.'
        ],
        paragraphLinks: [{
          label: 'Why Data May Be Missing or Delayed',
          slug: 'why-data-may-be-missing-or-delayed',
          section: 'use-the-symptom'
        }]
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['supported-platforms-and-cameras', 'ip-camera-configuration', 'network-security-and-previews'],
    legacy: false
  },
  {
    slug: 'brightsign-installation',
    title: 'BrightSign installation',
    summary: 'Prepare a supported BrightSign player and storage card, install the package for that model, and verify the camera and Device in the Dashboard.',
    category: 'deployment',
    sources: [{ file: 'brightsign-installation.pdf' }],
    sections: [
      {
        id: 'pre-installation',
        title: 'Prepare the player',
        paragraphs: [
          'Treat the BrightSign base setup and the AdMobilize package as two parts of the same installation. Confirm the exact player family and firmware before preparing the storage card, because a package or setup intended for another model may not start correctly.'
        ],
        steps: [
          'Confirm that the player model, firmware family, camera, storage card, and expanded-I/O hardware are approved for the deployment.',
          'Complete the BrightSign base setup and network configuration.',
          'Enable only the diagnostic or local management access required by the deployment policy.',
          'Create the BrightSign setup files and place them on the storage card.',
          'Restart the player and leave the prepared storage card installed.'
        ]
      },
      {
        id: 'prerequisites',
        title: 'Collect prerequisites securely',
        paragraphs: [
          'Before the Site visit, collect the identifiers, camera hardware, network access, and provisioning details the installation requires. Keep sensitive values in the approved deployment system instead of moving them through ordinary email or screenshots. Create the Device record first when your workflow uses BrightSign Device provisioning.'
        ],
        paragraphLinks: [{
          label: 'BrightSign Device provisioning',
          slug: 'brightsign-provisioning',
          section: 'requirements'
        }],
        bullets: [
          'Provide outbound web access required by the current BrightSign and AdMobilize deployment contracts.',
          'Record the player serial number and MAC address in the approved deployment system.',
          'Connect the supported USB camera through the required adapter.',
          'Do not send Dashboard credentials, camera credentials, or provisioning material through ordinary email.'
        ]
      },
      {
        id: 'install-package',
        title: 'Install the approved package',
        paragraphs: [
          'Use the current signed package supplied for the exact player family. A package built for another model or firmware family can leave the installation unusable, so verify both before writing to the storage card.'
        ],
        steps: [
          'Back up any presentation or media that must be retained.',
          'Prepare the storage card as directed for a new installation or upgrade.',
          'Upload the approved package to the root of the storage card.',
          'Reboot or power-cycle the player.',
          'Allow the extension to initialize before checking the camera indicator.',
          'Keep the AdMobilize directory on the storage card after installation.'
        ]
      },
      {
        id: 'verify',
        title: 'Verify the deployment',
        paragraphs: [
          'After the extension initializes, verify the installation from the player outward: camera indicator, Dashboard status, preview, and final camera position. If network access is restricted, compare the Site configuration with Network, credentials, and previews.'
        ],
        paragraphLinks: [{
          label: 'Network, credentials, and previews',
          slug: 'network-security-and-previews',
          section: 'outbound-access'
        }],
        steps: [
          'Confirm that the camera indicator becomes active after initialization.',
          'Open Devices and confirm that the new Device is online.',
          'Request a preview and use it to position the camera.',
          'If the Device stays offline, check power, Ethernet, camera connection, storage card, and the approved network allowlist.'
        ]
      },
      {
        id: 'recover',
        title: 'Recover a player that does not start',
        paragraphs: [
          'If the base setup is damaged, follow the factory-reset procedure published for the exact player model, then reapply the prepared setup files. Avoid using reset instructions from a different player family.',
          'A reset or storage-card format removes local configuration and media. Confirm the target and the available backups before proceeding.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Devices', to: '/devices' }],
    related: ['brightsign-provisioning', 'supported-platforms-and-cameras', 'network-security-and-previews'],
    legacy: false
  },
  {
    slug: 'network-security-and-previews',
    title: 'Network, credentials, and previews',
    summary: 'Give Devices the outbound access they need while protecting provisioning material, camera credentials, previews, and recordings.',
    category: 'deployment',
    sources: [{ file: 'ai-sensor-security.pdf' }],
    sections: [
      {
        id: 'access-control',
        title: 'Protect platform and Device access',
        paragraphs: [
          'Dashboard access uses authenticated, encrypted connections. Device provisioning and control rely on Device-specific API credentials and provisioning material, so operational access depends on keeping those values within the systems and people approved to use them.'
        ],
        bullets: [
          'Store Site Group and Device provisioning tokens in an approved secret system.',
          'Treat camera stream URLs as secrets when they contain credentials.',
          'Rotate a value if it may have been exposed.',
          'Do not place secrets in source control, documentation, screenshots, tickets, chat, or ordinary email.'
        ]
      },
      {
        id: 'outbound-access',
        title: 'Allow required outbound traffic',
        paragraphs: [
          'Managed edge Devices initiate outbound connections for name resolution, time synchronization, provisioning, configuration, model and software delivery, telemetry, and remote management. They do not require unsolicited inbound internet connections.',
          'The required hostnames vary with the operating system, package source, and management model. Obtain the current allowlist for the selected deployment path instead of copying a legacy wildcard list from another Site or runtime generation.'
        ],
        tables: [
          {
            caption: 'Common outbound services',
            headers: ['Purpose', 'Protocol'],
            rows: [
              ['DNS name resolution', 'UDP 53'],
              ['Time synchronization', 'UDP 123'],
              ['Platform APIs, updates, and managed control', 'TCP 443']
            ]
          }
        ]
      },
      {
        id: 'previews-and-recordings',
        title: 'Handle previews and recordings',
        paragraphs: [
          'A preview command returns an image from the Device so an authorized operator can verify the camera view. The current Audience configuration includes preview blur and blur-amount controls, but preview blur is not enabled by default.',
          'Limit access to previews and any enabled recordings. Apply the required blur, retention, and privacy policy before collecting operational images. Audience metrics explains how previews differ from the aggregate measurements shown in analytics.'
        ],
        paragraphLinks: [{
          label: 'Audience metrics',
          slug: 'audience-metrics',
          section: 'preview-and-privacy'
        }]
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['sensor-network-setup', 'audience-metrics', 'windows-installation'],
    legacy: false
  },
  {
    slug: 'sensor-network-setup',
    title: 'AI sensor network setup',
    summary: 'Connect an AI sensor to the Site network by Wi-Fi, Ethernet, or supported modem, then confirm the connection from the Dashboard.',
    category: 'deployment',
    sources: [{ file: 'ai-sensor-internet-configuration.pdf' }],
    sections: [
      {
        id: 'access',
        title: 'Open the connection interface',
        paragraphs: [
          'The sensor exposes a local configuration network so you can give it the Site connection details. From a nearby authorized computer or mobile Device, connect to that network and open the connection interface. Use the credentials supplied for this sensor instead of relying on a shared default password.'
        ],
        bullets: [
          'If the captive portal does not open, confirm that the client is still connected to the sensor network.',
          'If the sensor has already joined another network, obtain its assigned address through the approved network-management process.',
          'Do not record Device identifiers or network credentials in shared documentation.'
        ]
      },
      {
        id: 'wifi',
        title: 'Configure Wi-Fi',
        paragraphs: [
          'Use a Wi-Fi profile supplied or approved by the Site network administrator. Automatic addressing is the simplest path for most installations; enter static values only when the network design requires them.'
        ],
        steps: [
          'Select Wi-Fi and choose an available network.',
          'Run a new scan when the network is not listed; reconnect to the sensor network if scanning disconnects the client.',
          'For a manually configured or hidden network, enter the SSID, security type, and required authentication details.',
          'Open advanced settings only when a static address is required, then enter the gateway, address, subnet, and DNS values supplied by the network administrator.',
          'Save the connection and allow the sensor to join the network.'
        ]
      },
      {
        id: 'ethernet-modem',
        title: 'Configure Ethernet or modem access',
        paragraphs: [
          'Ethernet normally uses automatic addressing. When the Site requires a static configuration, use the gateway, address, subnet, and DNS values supplied by its network administrator. Do not copy values from another sensor.',
          'For supported modem hardware, select the country and carrier or enter the carrier settings supplied by the network administrator. Creating a new modem profile can replace the existing one, so confirm the current configuration first.'
        ]
      },
      {
        id: 'test',
        title: 'Test and manage the connection',
        paragraphs: [
          'After saving the profile, verify both sides of the connection: the sensor should join the Site network, and the Dashboard should report the Device online. If it remains unavailable, use Understanding Device Status to separate network, camera, and application checks.'
        ],
        paragraphLinks: [{
          label: 'Understanding Device Status',
          slug: 'understanding-device-status',
          section: 'investigate-a-status'
        }],
        steps: [
          'Submit the connection and wait for the sensor to leave its configuration network.',
          'Open the connection manager from a client on the same network.',
          'Review the saved connection and remove invalid profiles.',
          'Open Devices and confirm that the sensor is online.',
          'Request a preview to verify data-path and camera access.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Check Devices', to: '/devices' }],
    related: ['balena-sensor-setup', 'workspace-management'],
    legacy: false
  },
  {
    slug: 'balena-sensor-setup',
    title: 'AI sensor setup on BalenaOS',
    summary: 'Prepare and flash an approved BalenaOS sensor image, complete its first connection, and verify that it belongs to the right account.',
    category: 'deployment',
    sources: [{ file: 'ai-sensor-setup-on-balenaos.pdf' }],
    sections: [
      {
        id: 'requirements',
        title: 'Gather hardware and tools',
        paragraphs: [
          'Collect everything needed to open, image, and reconnect the sensor before powering it down. The current approved image and reliable Ethernet access are especially important because the Device completes its first update before you move it to the final Site network.'
        ],
        bullets: [
          'The supported sensor and its removable storage.',
          'A storage-card reader and the tools required to open the enclosure.',
          'The current approved AdMobilize image.',
          'An approved imaging application.',
          'Ethernet connectivity for the first update.'
        ]
      },
      {
        id: 'prepare',
        title: 'Prepare and flash the sensor',
        paragraphs: [
          'Flashing replaces the contents of the selected storage card. Back up any local configuration that must be retained, identify the card carefully, and let the imaging application finish verification before reinstalling it.'
        ],
        steps: [
          'Back up any local configuration that must be retained and power down the sensor.',
          'Open the enclosure and remove the storage card according to its hardware guide.',
          'Insert the card into the reader and select the approved image in the imaging application.',
          'Confirm the destination card before starting; flashing erases its existing contents.',
          'Wait for verification to complete, then reinstall the card and close the enclosure.'
        ]
      },
      {
        id: 'connect',
        title: 'Connect and assign the sensor',
        paragraphs: [
          'Use Ethernet for the first connection so the operating system and application can update on a stable network. Once the sensor has been assigned and appears online, AI sensor network setup explains how to move it to the connection used at the Site.'
        ],
        paragraphLinks: [{ label: 'AI sensor network setup', slug: 'sensor-network-setup', section: 'wifi' }],
        steps: [
          'Connect Ethernet and power on the sensor.',
          'Allow the operating system and application to complete their first update.',
          'Coordinate account assignment through the approved support process.',
          'Wait for assignment to complete, then confirm that the sensor is online in Devices.',
          'Change to the required site network only after the initial online check succeeds.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Check Devices', to: '/devices' }],
    related: ['sensor-network-setup', 'linux-on-device'],
    legacy: false
  },
  {
    slug: 'vehicle-analytics-setup',
    title: 'Vehicle Analytics setup on BalenaOS',
    summary: 'Assemble and image an approved BalenaOS host for Vehicle Analytics, then assign the Device and validate the Site camera.',
    category: 'deployment',
    sources: [{ file: 'vehicle-analytics-setup-on-balenaos.pdf' }],
    sections: [
      {
        id: 'requirements',
        title: 'Gather hardware and tools',
        paragraphs: [
          'Use a currently approved host, memory, SSD, camera, BalenaOS image, and installation medium. Confirm whether the Site uses an indoor USB camera or an outdoor IP camera before preparing the host, since that choice affects the hardware, network, and final validation.'
        ],
        bullets: [
          'A wired keyboard and monitor for firmware and boot-menu access.',
          'A storage-card reader and approved installation medium.',
          'Tools required to install the host memory and SSD.',
          'Ethernet using automatic addressing or verified static network details.'
        ]
      },
      {
        id: 'prepare-host',
        title: 'Prepare the host',
        paragraphs: [
          'Install the approved components with the host powered down, then set its firmware to recover automatically after a power interruption. That setting matters at unattended Sites where a brief outage should not require a visit.'
        ],
        steps: [
          'Power down the host and install the approved memory and SSD.',
          'Open firmware settings during startup.',
          'Set the host to power on automatically after a power failure.',
          'Save the firmware configuration and power down.'
        ],
        figures: [
          {
            src: 'assets/documentation/vehicle-power-setting.png',
            alt: 'Device firmware menu highlighting the automatic power-on setting',
            caption: 'Configure the host to power on automatically after an interruption.'
          }
        ]
      },
      {
        id: 'install-image',
        title: 'Install the approved image',
        paragraphs: [
          'Writing the approved image prepares both the operating system and the managed application environment. Disconnect unrelated removable storage and verify the target before flashing so the installer cannot overwrite the wrong drive.'
        ],
        steps: [
          'Flash the approved image to the installation medium and verify the destination before writing.',
          'Disconnect other removable storage from the host.',
          'Start the host and select the installation medium from the boot menu.',
          'Wait for the installer to shut the host down completely.',
          'Remove the installation medium, connect Ethernet, and power the host on.',
          'Allow the first operating-system and application update to finish.'
        ]
      },
      {
        id: 'assign',
        title: 'Assign and verify the Device',
        paragraphs: [
          'Assignment connects the new host with the correct account. After it appears online, finish the installation at the camera: Vehicle camera positioning and zones covers framing, regions, and validation against real traffic.'
        ],
        paragraphLinks: [{
          label: 'Vehicle camera positioning and zones',
          slug: 'vehicle-camera-positioning',
          section: 'field-of-view'
        }],
        steps: [
          'Coordinate account assignment through the approved support process.',
          'Provide static network settings only through the approved secure channel when the site does not use automatic addressing.',
          'Confirm that the assigned Device becomes online in Devices.',
          'Complete the site camera-positioning and zone-validation procedure.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Check Devices', to: '/devices' }],
    related: ['sensor-network-setup', 'linux-on-device'],
    legacy: false
  },
  {
    slug: 'linux-on-device',
    title: 'AdMobilize Vision on Linux',
    summary: 'Prepare a supported Linux host, install Vision from the authenticated package source, provision the Device, and trace data into the Dashboard.',
    category: 'deployment',
    sources: [
      { file: 'linux-on-device-guide.pdf' },
      { file: 'linux-on-device-guide (2).pdf' },
      { file: 'linux-installation.pdf' }
    ],
    sections: [
      {
        id: 'prepare',
        title: 'Prepare the Linux host',
        paragraphs: [
          'Start with the complete workload, not the operating system alone. Confirm the supported distribution, release, architecture, hardware, camera, storage, and network for the selected solution, and include any CMS or media-player work in host sizing. Supported platforms and cameras provides the planning context before installation.'
        ],
        paragraphLinks: [{
          label: 'Supported platforms and cameras',
          slug: 'supported-platforms-and-cameras',
          section: 'choose-platform'
        }],
        bullets: [
          'Create the AdMobilize account and Site Group before installation.',
          'Install required operating-system updates.',
          'Confirm outbound access to the current package and runtime services.',
          'Connect the camera and confirm that the operating system can access it.'
        ]
      },
      {
        id: 'install',
        title: 'Install from the authenticated package source',
        paragraphs: [
          'AdMobilize Vision is distributed through an authenticated APT source. Obtain the current repository bootstrap and package command from the authorized release channel because distribution names, signing keys, and package names can change. Avoid reusing repository instructions saved from an older deployment.'
        ],
        steps: [
          'Add the current repository signing key using the approved keyring path.',
          'Add the repository entry that matches the supported distribution and release.',
          'Refresh the package index and install the approved Vision package.',
          'Run the installed client help or version command to confirm that the command-line client is available.',
          'When required by the runtime, add the operator to its local engine group and sign out and back in.'
        ]
      },
      {
        id: 'components',
        title: 'Understand the Linux components',
        paragraphs: [
          'The Linux installation separates local control, model processing, buffering, and platform communication into distinct components. Their status helps you locate a problem before changing the configuration or provisioning the Device again.'
        ],
        tables: [
          {
            caption: 'Vision components',
            headers: ['Component', 'Purpose'],
            rows: [
              ['admclient', 'Configures the installation and controls its services.'],
              ['malos-vision', 'Runs the computer-vision models.'],
              ['redis', 'Buffers data when the Device cannot reach the platform.'],
              ['admprovider', 'Provisions the Device, receives configuration, and sends data to the platform.']
            ]
          }
        ]
      },
      {
        id: 'provision',
        title: 'Provision the Device',
        paragraphs: [
          'Provisioning connects the Linux installation with its Dashboard record. A new installation uses Site Group provisioning material to create a Device, while a replacement uses that material with the Device ID of an existing Device.',
          'Generate the current command through the approved provisioning flow for this host. Do not reuse a command created for another installation, and remove its secret material from terminal history and shared notes after use.'
        ],
        steps: [
          'Choose whether to create a new Device or provision an existing record.',
          'Select the Site Group, Device name, and solution application.',
          'Run the generated command locally on the target host.',
          'Clear provisioning material from terminal history and shared notes.',
          'Confirm that the expected camera opens and the provider starts.'
        ]
      },
      {
        id: 'verify',
        title: 'Verify local and cloud data',
        paragraphs: [
          'Follow the data path in order: component status, model startup, camera connection, local detections, and finally recent Dashboard data for the same Device. This keeps a local camera or runtime issue from being confused with a filter or delivery delay.'
        ],
        steps: [
          'Use the client status command to review the components.',
          'Follow the Vision service logs only long enough to confirm model startup, camera connection, and local detections.',
          'Do not copy operational logs containing identifiers or detections into shared documentation.',
          'Open Dashboard analytics for the same Device and confirm that recent data arrives.'
        ]
      },
      {
        id: 'upgrade',
        title: 'Upgrade the installation',
        paragraphs: [
          'Models can update independently from the other runtime components. Use the current client upgrade procedure for those components, then stop and start services only as directed by the release instructions for the installed version.'
        ]
      },
      {
        id: 'network-access',
        title: 'Allow required outbound traffic',
        paragraphs: [
          'Linux installations initiate outbound connections for packages, registries, Device management, models, time synchronization, telemetry, and software delivery. Arrange that access before installation when the Site uses a restricted network.',
          'Obtain the current Linux allowlist for the installed runtime because package and runtime hostnames can change across releases. Network, credentials, and previews explains the shared access and secret-handling model.'
        ],
        paragraphLinks: [{
          label: 'Network, credentials, and previews',
          slug: 'network-security-and-previews',
          section: 'outbound-access'
        }]
      },
      {
        id: 'troubleshoot',
        title: 'Troubleshoot common Linux issues',
        paragraphs: [
          'Start with the symptom closest to where the data path stops. If local detections are healthy, for example, check buffering, provider status, and network access before changing the camera or model configuration.'
        ],
        tables: [
          {
            caption: 'Linux checks',
            headers: ['Symptom', 'Checks'],
            rows: [
              ['Permission denied when running the client', 'Confirm membership in the required local engine group, then start a new login session.'],
              ['Local detections but no Dashboard data', 'Check the selected Device and date range, network access, buffered data, and provider status.'],
              ['Name-resolution or broker retries', 'Check DNS, packet loss, and outbound access; detections can buffer locally during a temporary outage.'],
              ['Runtime disconnect during install', 'Confirm that the distribution and release are supported.'],
              ['Runtime network missing after upgrade', 'Use only the current recovery procedure supplied for the installed release before reprovisioning.']
            ]
          }
        ]
      }
    ],
    dashboardLinks: [{ label: 'Check Devices', to: '/devices' }],
    related: ['on-device-data-stream', 'sensor-network-setup'],
    legacy: false
  },
  {
    slug: 'brightsign-provisioning',
    title: 'BrightSign Device provisioning',
    summary: 'Create the Dashboard Device record that connects a specific BrightSign player, solution, and Site Group to the installation workflow.',
    category: 'deployment',
    sources: [{ file: 'create-brightsign-device.pdf' }],
    sections: [
      {
        id: 'requirements',
        title: 'Gather prerequisites',
        paragraphs: [
          'Provisioning starts with an authenticated API request and the identifiers printed by the physical player. Use Authentication v2 to obtain the access token, then confirm the target Site Group, serial number, MAC address, display name, and supported solution before creating the record.',
          'Store the access token, serial number, MAC address, and returned provisioning material in approved systems. Do not place them in screenshots or shared documentation.'
        ],
        paragraphLinks: [{ label: 'Authentication v2', slug: 'authentication-v2', section: 'login' }],
        bullets: [
          'An access token from the current authentication flow.',
          'The target Site Group ID from Dashboard Profile.',
          'The player serial number and MAC address.',
          'A display name and a solution currently supported on the player.'
        ]
      },
      {
        id: 'request',
        title: 'Create the Device record',
        paragraphs: [
          'Send an authenticated Device-creation request to the current Device Management API route for the target Site Group. The request marks the record as a BrightSign Device and supplies the player identifiers and selected solution. A recognizable display name will make the player easier to find during installation and later support.'
        ],
        tables: [
          {
            caption: 'Request fields',
            headers: ['Field', 'Purpose'],
            rows: [
              ['display name', 'A recognizable name for Dashboard Users.'],
              ['BrightSign marker', 'Identifies the Device type for the provisioning workflow.'],
              ['MAC address', 'Matches the record to the player network interface.'],
              ['Serial number', 'Matches the record to the physical player.'],
              ['Solution', 'Selects a currently supported Vision application.']
            ]
          }
        ]
      },
      {
        id: 'response',
        title: 'Protect the response',
        paragraphs: [
          'A successful response includes the created Device identity, Site Group, labels, solution, state, and provisioning material. Store the response securely and expose only the non-secret fields needed by the installation workflow. Continue with BrightSign installation after confirming that the record matches the intended player.'
        ],
        paragraphLinks: [{
          label: 'BrightSign installation',
          slug: 'brightsign-installation',
          section: 'pre-installation'
        }],
        steps: [
          'Confirm that the response belongs to the intended Site Group and player.',
          'Store the provisioning material in the approved secret system.',
          'Open Devices and confirm that the new record has the expected name and solution.',
          'Continue with the approved BrightSign installation package for that player.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['authentication-v2', 'linux-on-device'],
    legacy: false
  }
];
